import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { paymentAPI } from '../lib/api';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const attemptsRef = useRef(0);
  const maxAttempts = 5;

  const pollPaymentStatus = useCallback(async (sessionId) => {
    if (attemptsRef.current >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await paymentAPI.getStatus(sessionId);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
      } else if (response.data.status === 'expired') {
        setStatus('expired');
      } else {
        attemptsRef.current += 1;
        setTimeout(() => pollPaymentStatus(sessionId), 2000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      attemptsRef.current += 1;
      setTimeout(() => pollPaymentStatus(sessionId), 2000);
    }
  }, []);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus('error');
    }
  }, [searchParams, pollPaymentStatus]);

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center animate-fade-in-up">
        {status === 'checking' && (
          <>
            <div className="w-20 h-20 bg-zinc-100 mx-auto flex items-center justify-center mb-6">
              <Loader2 className="w-10 h-10 text-zinc-950 animate-spin" strokeWidth={1.5} />
            </div>
            <h1 className="text-2xl font-bold text-zinc-950" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Verifying Payment...
            </h1>
            <p className="mt-2 text-zinc-500">Please wait while we confirm your payment.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 bg-green-500 mx-auto flex items-center justify-center mb-6">
              <CheckCircle className="w-10 h-10 text-white" strokeWidth={1.5} />
            </div>
            <h1 className="text-2xl font-bold text-zinc-950" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Payment Successful!
            </h1>
            <p className="mt-2 text-zinc-500">
              Your plan has been upgraded. Thank you for your purchase!
            </p>
            <Link
              to="/dashboard"
              className="btn-primary inline-block mt-6"
              data-testid="go-to-dashboard-btn"
            >
              Go to Dashboard
            </Link>
          </>
        )}

        {(status === 'error' || status === 'expired' || status === 'timeout') && (
          <>
            <div className="w-20 h-20 bg-red-500 mx-auto flex items-center justify-center mb-6">
              <XCircle className="w-10 h-10 text-white" strokeWidth={1.5} />
            </div>
            <h1 className="text-2xl font-bold text-zinc-950" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {status === 'timeout' ? 'Verification Timeout' : 'Payment Failed'}
            </h1>
            <p className="mt-2 text-zinc-500">
              {status === 'timeout' 
                ? 'Please check your email for payment confirmation.'
                : 'Something went wrong with your payment. Please try again.'}
            </p>
            <div className="flex gap-4 justify-center mt-6">
              <Link
                to="/pricing"
                className="btn-secondary"
                data-testid="try-again-btn"
              >
                Try Again
              </Link>
              <Link
                to="/dashboard"
                className="btn-primary"
              >
                Go to Dashboard
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
